// * Import 3rd party lib
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { Box, Stack, styled, Grid, Popover, Button } from "@mui/material";

import APP_CONSTANTS from "../../../utils/config";
import StyledTypography from "../../../kit/Typography";
import { rapidStyles } from "../../../theme/style";
import { getStatusButton } from "../../../utils/utils";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useDispatch } from "react-redux";
import { updateAlertInfo } from "../../../redux/slice/userSlice";
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditIcon from '@mui/icons-material/Edit';
const ReportTypographyHeading = styled(StyledTypography)(({ theme }) => ({
  fontWeight: theme.fontWeight[800],
  fontSize: theme.size[16],
  lineHeight: theme.size[30],
  whiteSpace: "nowrap",
  width: theme.size[150],
  overflow: "hidden",
  textOverflow: "ellipsis",
  color: theme.palette.cards.heading,
}));

const ReportTypographyDesc = styled(StyledTypography)(({ theme }) => ({
  fontWeight: theme.fontWeight[500],
  fontSize: theme.size[13],
  lineHeight: theme.size[24],
  minWidth: "max-content",
  color: theme.palette.cards.heading,
  marginRight: theme.size[4],
}));

const ReportTypographyTitle = styled(StyledTypography)(({ theme }) => ({
  fontWeight: theme.fontWeight[400],
  fontSize: theme.size[11],
  lineHeight: theme.size[20],
  WebkitLineClamp: "1",
  WebkitBoxOrient: "vertical",
  display: "block",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  color: theme.palette.cards.subheading,
}));

const ReportTypographyDescription = styled(StyledTypography)(({ theme }) => ({
  fontWeight: theme.fontWeight[400],
  fontSize: theme.size[11],
  lineHeight: theme.size[20],
  WebkitLineClamp: 3,
  minHeight: `calc(${theme.size[20]} * 3)`,
  WebkitBoxOrient: "vertical",
  display: "-webkit-box",
  overflow: "hidden",
  textOverflow: "ellipsis",
  color: theme.palette.cards.subheading,
}));

const dateRender = (params) => {
  const { value } = params;
  const date = new Date(value);

  const options = { year: "numeric", month: "long", day: "numeric" };
  const formattedDate = date.toLocaleDateString("en-US", options);
  return formattedDate
};

const RequestIdPopup = ({ anchorEl, request_id, closePopup, theme }) => {
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();

  const copyToClipboard = (text_data) => {
    navigator.clipboard
      .writeText(text_data)
      .then(() => {
        dispatch(
          updateAlertInfo({
            message: "Copied",
            open: true,
            severity: "success",
          })
        );
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={closePopup}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
    >
      <Box
        sx={{
          padding: "6px 10px",
          maxWidth: 450,
          display: "flex",
          flexDirection: "row",
        }}
      >
        <ReportTypographyTitle>Request ID: &nbsp;</ReportTypographyTitle>
        <ReportTypographyDesc variant="body2">
          {request_id}
        </ReportTypographyDesc>
        <ContentCopyIcon
          sx={{
            marginLeft: theme.size[4],
            fontSize: theme.size[16],
            color: theme.palette.secondary.main,
            cursor: "pointer",
          }}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            copyToClipboard(request_id);
          }}
        />
      </Box>
    </Popover>
  );
};
// * Function
function GridViewCard(props = {}) {
  const { app = {} } = props;
  const theme = useTheme();
  const classes = rapidStyles(theme);
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminPortal = location.pathname.includes("admin");

  const [anchorEl, setAnchorEl] = React.useState(null);
  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleClickTile = async () => {
    // gaEventTracker("rapid_application_tile_clicked", `app_id = ${app.id}`);
    const status = app.status;
    if (
      status === APP_CONSTANTS.RAPID_STATUS.TECHNICAL_OWNER_RESPONSE_PENDING ||
      status === APP_CONSTANTS.RAPID_STATUS.PROCESS_INITIATED
    ) {
      navigate(`/rapid/edit-rapidApplication/${app.request_id}`);
    } else if (isAdminPortal) {
      navigate(`/rapid/register-rapidApplication/admin/${app.request_id}`);
    } else {
      navigate(`/rapid/register-rapidApplication/${app.request_id}`);
    }
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent click from bubbling to parent
    navigate(`/rapid/edit-finished-rapidApplication/${app.request_id}`);
    // if (isAdminPortal) {
    //     navigate(`/rapid/edit-finished-rapidApplication/admin/${app.request_id}`);
    //   } else {
    //     navigate(`/rapid/edit-finished-rapidApplication/${app.request_id}`);
    //   }
  };

  return (
    <Grid item>
      <Box className={classes.gridViewCard} id="grd">
        <Stack>
          <Stack>
            <ReportTypographyHeading
              title={app.app_name}
              sx={{ fontWeight: 500, cursor: "pointer" }}
              onClick={() => handleClickTile()}
            >
              {app.app_name}
            </ReportTypographyHeading>
            <ReportTypographyDescription title={app.app_description} onClick={() => handleClickTile()}>
              {app.app_description === "" ? "-" : app.app_description}
            </ReportTypographyDescription>
            <Stack
              direction="row"
              sx={{
                paddingTop: theme.size[8],
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexBasis: "45%",
                }}
              >
                <ReportTypographyTitle>Project Name</ReportTypographyTitle>
                <ReportTypographyDesc title={app.project_name} onClick={() => handleClickTile()}>
                  {app.project_name === "" ? "-" : app.project_name}
                </ReportTypographyDesc>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexBasis: "55%",
                  paddingLeft: "10%",
                }}
              >
                <ReportTypographyTitle>Request ID</ReportTypographyTitle>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <ReportTypographyDesc
                    title={app.request_id}
                    sx={{
                      cursor: "pointer",
                      color: theme.palette.secondary.main,
                      display: "flex", // Ensure items are aligned in a row
                      alignItems: "center", // Align icon and text vertically
                      fontWeight:500
                    }}
                    onClick={handleOpen}
                  >
                    <VisibilityOutlinedIcon sx={{fontSize:15 }}/>
                    &nbsp;
                    View ID
                  </ReportTypographyDesc>
                  <RequestIdPopup
                    anchorEl={anchorEl}
                    request_id={app.request_id}
                    closePopup={handleClose}
                    theme={theme}
                  />
                </Box>
              </Box>
            </Stack>
            <Stack
              direction="row"
              sx={{
                paddingTop: theme.size[8],
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
              onClick={() => handleClickTile()}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexBasis: "45%",
                }}
              >
                <ReportTypographyTitle>Created On&nbsp;</ReportTypographyTitle>
                <ReportTypographyDesc title={"Created On"}>
                  {dateRender({ value: app.created_at })}
                </ReportTypographyDesc>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexBasis: "55%",
                  paddingLeft: "10%",
                }}
              >
                <ReportTypographyTitle>Updated On&nbsp;</ReportTypographyTitle>
                <ReportTypographyDesc title={"Updated On"}>
                  {dateRender({ value: app.updated_at })}
                </ReportTypographyDesc>
              </Box>
            </Stack>
            <Stack
              direction="row"
              sx={{
                padding: "12px 0 4px  0",
              }}
              onClick={() => handleClickTile()}
            >
              {getStatusButton(app.status)}
            </Stack>
            <Stack
              direction="row"
              sx={{
                padding: "12px 0 4px 0",
                position: "absolute",
                top: theme.size[10],
                right: theme.size[10],
                zIndex: 1,
              }}
            >
              <Button
                onClick={handleClick}
                sx={{
                  color: theme.palette.primary.main,
                  pointerEvents: 'auto',
                  minWidth: 40,
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  display: app.status === APP_CONSTANTS.RAPID_STATUS.FINISHED ? 'block' : 'none',
                  // backgroundColor: theme.palette.primary.main,
                  // color: theme.palette.common.white,
                  // fontWeight: theme.fontWeight[500],
                  // pointerEvents: 'auto',
                  // textTransform: 'none',
                  // minWidth: 0,
                  // padding: '4px 12px',
                  // '&:hover': {
                  //   backgroundColor: theme.palette.primary.main, // Disable hover effect
                  // },
                }}
              >
                <EditIcon />
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Grid>
  );
}

export default GridViewCard;
